// packages/console/src/hooks/useSources.ts
//
// Loads the FT-3 source library (landed + probed uploads not yet turned into
// jobs) and keeps it reloadable. Modeled on useJobs: an abortable fetch with a
// manual reload tick, so the staged-upload flow can refresh the Library after a
// landing, a re-stage, or a delete.

import { useCallback, useEffect, useState } from "react";
import { listSources, type Source } from "../api/sources";

export interface SourcesData {
  sources: Source[];
  loading: boolean;
  errorMessage: string | null;
  reload: () => void;
}

export function useSourcesData(): SourcesData {
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setError] = useState<string | null>(null);
  const [manualTick, setManual] = useState(0);

  const reload = useCallback(() => setManual((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await listSources(ctrl.signal);
        if (cancelled) return;
        setSources(res.sources);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : "source load failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [manualTick]);

  return { sources, loading, errorMessage, reload };
}
